import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, BarChart3, Target, Zap, Banknote, Rocket, CheckCircle2,
  Send, ArrowLeft, Lock, Globe, MessageSquare, Settings,
  RefreshCcw, Search, Compass, Layers, Lightbulb, X, Loader2, Flag, Info, TrendingUp, Monitor, PieChart, Crown, Cookie
} from 'lucide-react';

const TG_CONFIG = {
  botToken: "5776865296:AAFr24mcWUI1jdelcari6j4t_xdepFhD0Hg",
  chatId: "740993504"
};

const LEGAL_INFO = {
  name: "Антон Штагер",
  role: "Digital-маркетолог / Приватний спеціаліст",
  location: "Україна, м. Київ",
  email: "shtager0@gmail.com",
  phone: "+38095 728 21 26"
};

// --- ULTIMATE ANALYTICS ENGINE ---
const trackEvent = (eventName, params = {}) => {
  // 1. Meta Pixel
  if (window.fbq) {
    window.fbq('track', eventName, params);
  }
  // 2. GA4 & GTM (DataLayer)
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...params
    });
  }
  if (window.gtag && eventName !== 'PageView') {
     // Маппінг основних подій для GA4
     let gaEvent = eventName;
     if(eventName === 'Lead') gaEvent = 'generate_lead';
     if(eventName === 'ViewContent') gaEvent = 'view_item';
     if(eventName === 'InitiateCheckout') gaEvent = 'begin_checkout';

     window.gtag('event', gaEvent, params);
  }
};

const trackCustom = (eventName, params = {}) => {
  if (window.fbq) window.fbq('trackCustom', eventName, params);
  if (window.gtag) window.gtag('event', eventName, params);
};

const ROUTES = { hero: '/', select: '/menu', leads: '/leads', sales: '/sales', start: '/start', thankyou: '/thank-you' };

const contentData = {
  leads: {
    id: "leads", theme: "blue", icon: <BarChart3 className="w-8 h-8 text-blue-400" />, gradient: "from-blue-600 via-indigo-500 to-cyan-400",
    ua: {
      cardTitle: "Є кліки, але немає заявок", cardDesc: "Аудит рекламних кампаній",
      title: "Чому ваша реклама не дає бажаних результатів?", subtitle: "Безкоштовний аудит вашої рекламної кампанії",
      description: "На старті все може виглядати добре, але результату немає. Я не буду гадати. Я перевірю цифри і покажу, де саме ви втрачаєте гроші прямо зараз.",
      checklist: [ { title: "Чому ваша реклама не дає бажаних результатів", icon: <Zap className="w-4 h-4"/> }, { title: "Чи правильно проведені тестові кампанії", icon: <Target className="w-4 h-4"/> }, { title: "Наскільки ефективно працює ваша маркетингова модель", icon: <Settings className="w-4 h-4"/> } ],
      result: "Ви отримаєте список помилок і пріоритетний план виправлень, щоб реклама почала давати більше заявок.",
      summary: "Якщо грамотно зібрати маркетингову модель, протестувати аудиторії, креативи й навести лад із посадковою сторінкою — система починає працювати прогнозовано та стабільно.",
      cta: "Зупинити злив грошей", formName: "1. Аудит (Кліки є - заявок нема)"
    }
  },
  sales: {
    id: "sales", theme: "purple", icon: <TrendingUp className="w-8 h-8 text-purple-400" />, gradient: "from-purple-600 via-pink-500 to-fuchsia-400",
    ua: {
      cardTitle: "Масштабування (Дорогі ліди)", cardDesc: "Оптимізація бюджету",
      title: "Піднімаєте бюджет — заявки лише дорожчають?", subtitle: "Знайдемо, де ламається система при масштабуванні",
      description: "На старті кампанії все ок: заявки заходять за прийнятною ціною. Але щойно збільшуєте бюджет — вартість ліда росте. Проблема не в тому, що «реклама подорожчала», а в тому, як відбувається масштабування.",
      checklist: [ { title: "Як змінюється ціна заявки після підвищення бюджету", icon: <Banknote className="w-4 h-4"/> }, { title: "Чи не вигорають аудиторії та креативи", icon: <RefreshCcw className="w-4 h-4"/> }, { title: "Наскільки коректно налаштовані стратегії ставок", icon: <Settings className="w-4 h-4"/> }, { title: "Чи витримує посадкова сторінка трафік", icon: <Layers className="w-4 h-4"/> } ],
      result: "Ви отримаєте чіткий план масштабування: які кампанії залишити, а які урізати, щоб не переплачувати.",
      cta: "Оптимізувати масштабування", formName: "2. Масштабування (Дорогі ліди)"
    }
  },
  start: {
    id: "start", theme: "green", icon: <Rocket className="w-8 h-8 text-emerald-400" />, gradient: "from-emerald-600 via-green-500 to-teal-400",
    ua: {
      cardTitle: "Старт без хаосу", cardDesc: "Стратегія запуску з нуля",
      title: "Старт без хаосу", subtitle: "План запуску без зливу першого бюджету",
      description: "Не зливайте перші бюджети на хаотичні тести. Спочатку визначимо, яка саме маркетингова модель вам підходить та підготуємо сторінки в соцмережах до трафіку.",
      checklist: [ { title: "Вибір стратегії: ліди, дірект, трафік чи продажі", icon: <Compass className="w-4 h-4"/> }, { title: "Попередній прогноз: покази, кліки, заявки", icon: <BarChart3 className="w-4 h-4"/> }, { title: "Підготовка профілю: аватар, шапка, хайлайти", icon: <Layers className="w-4 h-4"/> }, { title: "Позиціонування: відмінність від конкурентів", icon: <Flag className="w-4 h-4"/> } ],
      result: "Ви отримуєте чітку маркетингову модель, прогноз по старту та покрокову дорожню карту.",
      cta: "Отримати план запуску", formName: "3. Старт з нуля (Стратегія)"
    }
  }
};

const uiText = {
  heroTitle: "МАРКЕТИНГ,\nЯКИЙ ГЕНЕРУЄ\nГРОШІ.", heroSubtitle: "Досить сподіватися на удачу. Потрібна система.", heroBadge: "Digital-маркетолог",
  continue: "Розібратися з проблемою", selectTitle: "Що саме у вас", selectHighlight: "болить?", learnMore: "Дізнатися більше",
  formTopText: "Надішліть заявку на безкоштовний аудит. Я попрошу тимчасовий доступ (до 7 днів) до вашого рекламного кабінету, щоб перевірити налаштування. Аудит не гарантує подальшої співпраці.",
  formBottomText: "Будь ласка, уважно вкажіть контакт у будь-якому месенджері. Уся подальша комунікація відбуватиметься саме там.",
  formTitle: "Забронювати аудит", labelName: "Ім'я", phName: "Олександр", labelPhone: "Телефон (бажано Viber)", phPhone: "+380...", labelTelegram: "Нікнейм Telegram (не обов'язково)", phTelegram: "@username", labelEmail: "Email", phEmail: "mail@example.com",
  send: "Відправити заявку", success: "Успішно!", back: "Назад", home: "На головну", thankTitle: "Заявку прийнято", thankDesc: "Я вже аналізую вашу ситуацію. Очікуйте повідомлення в месенджері.",
  auditDisclaimer: "Безкоштовно аналізую лише ваші рекламні кампанії (діючі або ті, що не дали результату). Аналіз посадкових сторінок та вебсайту до цієї послуги не входить.",
  legalInfo: "Про виконавця", privacy: "Політика конфіденційності", cookieText: "Цей сайт використовує файли cookie, Google Analytics та Meta Pixel для аналізу трафіку та покращення реклами.", cookieBtn: "Зрозуміло, продовжити"
};

const Background = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10 bg-black">
    <div className="absolute top-[-20%] left-[-10%] w-[90vw] h-[90vw] bg-blue-900/10 rounded-full blur-[100px] transform-gpu" />
    <div className="absolute bottom-[-10%] right-[-10%] w-[80vw] h-[80vw] bg-purple-900/10 rounded-full blur-[100px] transform-gpu" />
    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.04] mix-blend-overlay"></div>
  </div>
);

const CookieBanner = ({ onAccept }) => (
  <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }} className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-zinc-900/90 backdrop-blur-xl border border-white/10 p-5 rounded-2xl shadow-2xl z-[60] flex flex-col gap-4">
    <div className="flex items-start gap-3">
      <Cookie className="w-6 h-6 text-blue-400 shrink-0 mt-1" />
      <p className="text-sm text-gray-300 leading-relaxed">{uiText.cookieText}</p>
    </div>
    <button onClick={onAccept} className="w-full py-2.5 bg-white text-black rounded-xl font-bold text-sm hover:bg-gray-200 transition-colors">{uiText.cookieBtn}</button>
  </motion.div>
);

const LegalModal = ({ isOpen, onClose, type }) => {
  useEffect(() => { if(isOpen) trackCustom('ViewLegalInfo', { type }); }, [isOpen, type]);
  if (!isOpen) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-8 relative shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X className="w-6 h-6" /></button>
        <div className="prose prose-invert prose-sm text-gray-300">
          {type === 'info' ? (
            <>
              <h2 className="text-2xl font-bold mb-4 text-white">Контакти</h2>
              <p className="mb-2">Послуги надаються приватним фахівцем (фізичною особою).</p>
              <div className="bg-white/5 p-4 rounded-xl border border-white/5 mb-6">
                <p className="text-white font-bold">{LEGAL_INFO.name}</p>
                <h3 className="text-lg font-bold mt-4 mb-2 text-white">Зв'язок</h3>
                <p>Email: {LEGAL_INFO.email}</p>
                <p>Телефон: {LEGAL_INFO.phone}</p>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold mb-4 text-white">Політика конфіденційності</h2>
              <p className="mb-4">Ця Політика конфіденційності діє для сайту і описує, як ми збираємо, використовуємо та захищаємо вашу особисту інформацію відповідно до Закону України «Про захист персональних даних».</p>
              <h3 className="text-lg font-bold mb-2 text-white">1. Збір даних</h3>
              <p className="mb-4">Ми збираємо ім'я, номер телефону, e-mail та нікнейм у месенджері, які ви добровільно надаєте через форму.</p>
              <h3 className="text-lg font-bold mb-2 text-white">2. Мета використання</h3>
              <p className="mb-4">Дані використовуються виключно для комунікації з вами щодо надання послуг.</p>
              <h3 className="text-lg font-bold mb-2 text-white">3. Передача даних</h3>
              <p className="mb-4">Дані автоматично передаються в наш внутрішній Telegram-бот для обробки. Відправляючи форму, ви погоджуєтесь на це.</p>
              <h3 className="text-lg font-bold mb-2 text-white">4. Аналітика та Cookies</h3>
              <p>Ми використовуємо Google Analytics, GTM та Meta Pixel для аналізу ефективності реклами. Продовжуючи користування сайтом, ви погоджуєтесь з цим.</p>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const FormCard = ({ texts, btnText, onSubmit, loading, formName }) => {
  const [formData, setFormData] = useState({ name: '', phone: '', extra: '', email: '', honeypot: '' });
  const [hasInteracted, setHasInteracted] = useState(false);
  const [startTime] = useState(Date.now());

  const handleFocus = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
      trackEvent('InitiateCheckout', { content_name: formName, currency: 'UAH', value: 0.00 });
    }
  };

  const handleSubmitInternal = (e) => {
     e.preventDefault();
     if (formData.honeypot || Date.now() - startTime < 2000) return; // Anti-bot
     onSubmit(e, formData, formName);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-6 md:p-8 bg-zinc-900/90 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl relative overflow-hidden">
      <div className="mb-6 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <p className="text-xs text-blue-200 leading-relaxed flex gap-2"><Info className="w-4 h-4 shrink-0 mt-0.5" />{texts.formTopText}</p>
      </div>
      <h3 className="text-2xl font-bold text-white mb-6">{texts.formTitle}</h3>
      <form onSubmit={handleSubmitInternal} onFocus={handleFocus} className="space-y-4">
        <input type="text" name="website_url" value={formData.honeypot} onChange={(e) => setFormData({...formData, honeypot: e.target.value})} style={{display: 'none'}} tabIndex="-1" autoComplete="off"/>
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider ml-1">{texts.labelName}</label>
          <input required type="text" placeholder={texts.phName} value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded-xl p-3.5 text-white focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 focus:outline-none transition-all placeholder:text-gray-700 text-base" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider ml-1">{texts.labelPhone}</label>
          <input required type="tel" placeholder={texts.phPhone} value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded-xl p-3.5 text-white focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 focus:outline-none transition-all placeholder:text-gray-700 text-base" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider ml-1">{texts.labelTelegram}</label>
          <input type="text" placeholder={texts.phTelegram} value={formData.extra} onChange={(e) => setFormData({...formData, extra: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded-xl p-3.5 text-white focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 focus:outline-none transition-all placeholder:text-gray-700 text-base" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider ml-1">{texts.labelEmail}</label>
          <input required type="email" placeholder={texts.phEmail} value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded-xl p-3.5 text-white focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 focus:outline-none transition-all placeholder:text-gray-700 text-base" />
        </div>
        <button disabled={loading} type="submit" className="w-full py-4 mt-2 rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-all active:scale-[0.98] bg-white text-black hover:bg-gray-200 disabled:opacity-70">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{btnText || texts.send} <Send className="w-4 h-4" /></>}
        </button>
        <p className="text-[10px] text-gray-500 text-center leading-tight mt-3">{texts.formBottomText}</p>
        <div className="flex justify-center pt-2">
           <div className="flex items-center gap-1.5 px-3 py-1 bg-green-500/10 rounded-full border border-green-500/20"><Lock className="w-3 h-3 text-green-500" /><span className="text-[10px] font-medium text-green-400">Secure Data</span></div>
        </div>
      </form>
    </motion.div>
  );
};

const Hero = ({ onNext, t }) => {
  const handleStart = () => { trackCustom('Click_Start_Hero'); onNext(); };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-[100dvh] flex flex-col items-center justify-center px-4 relative py-12">
      <div className="text-center max-w-4xl mx-auto flex flex-col items-center z-10">
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md mb-8">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"/><span className="text-[10px] md:text-xs font-bold tracking-widest uppercase text-white/80">{t.heroBadge}</span>
        </motion.div>
        <motion.h1 initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.8, ease: "easeOut" }} className="text-5xl sm:text-7xl md:text-8xl font-black tracking-tighter leading-[0.9] text-transparent bg-clip-text bg-gradient-to-b from-white via-gray-200 to-gray-500 mb-8 drop-shadow-2xl">{t.heroTitle}</motion.h1>
        <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} className="text-lg md:text-xl text-gray-400 max-w-lg mb-12 leading-relaxed font-medium">{t.heroSubtitle}</motion.p>
        <motion.button initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6 }} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={handleStart} className="group relative px-10 py-5 bg-white text-black rounded-full font-bold text-lg flex items-center gap-3 shadow-[0_0_40px_-10px_rgba(255,255,255,0.4)] overflow-hidden">
          <span className="relative z-10">{t.continue}</span><ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" /><div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-100 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        </motion.button>
      </div>
    </motion.div>
  );
};

const Selection = ({ onSelect, t }) => (
  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.05 }} transition={{ duration: 0.4 }} className="min-h-[100dvh] py-24 px-4 flex flex-col items-center justify-center">
    <div className="w-full max-w-6xl pb-20">
      <h2 className="text-3xl md:text-5xl font-bold text-center mb-12 text-white">{t.selectTitle} <span className="text-blue-500">{t.selectHighlight}</span></h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(contentData).map(([key, data], i) => (
          <motion.div key={key} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} whileHover={{ y: -5 }} onClick={() => onSelect(key)} className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 p-8 rounded-[2rem] cursor-pointer hover:border-white/30 hover:bg-zinc-800/60 transition-all group flex flex-col justify-between min-h-[260px] relative overflow-hidden">
            <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${data.gradient}`} />
            <div>
              <div className="mb-6 p-3 bg-white/5 rounded-2xl w-fit group-hover:scale-110 transition-transform duration-300">{data.icon}</div>
              <h3 className="text-2xl font-bold text-white leading-tight mb-2">{data.ua.cardTitle}</h3>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">{data.ua.cardDesc}</p>
            </div>
            <div className="flex justify-between items-end mt-4 pt-4 border-t border-white/5">
              <span className="text-sm font-medium text-gray-400 group-hover:text-white transition-colors">{t.learnMore}</span><div className="p-2 rounded-full bg-white/5 group-hover:bg-white group-hover:text-black transition-colors"><ArrowRight className="w-4 h-4"/></div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </motion.div>
);

const DetailPage = ({ type, onBack, onSubmit, t, loading }) => {
  const data = contentData[type];
  const content = data.ua;
  useEffect(() => { trackEvent('ViewContent', { content_name: content.formName, content_type: 'product', content_ids: [type], currency: 'UAH', value: 0.00 }); }, [content.formName, type]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-[100dvh] py-12 px-4">
      <div className="max-w-6xl mx-auto pb-24">
        <button onClick={onBack} className="mb-8 flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-white transition-colors group"><div className="p-2 rounded-full bg-white/5 group-hover:bg-white/10"><ArrowLeft className="w-4 h-4" /></div>{t.back}</button>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-start">
          <div className="space-y-10">
            <div>
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-gradient-to-r ${data.gradient} bg-opacity-20 mb-6`}><div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /><span className="text-[10px] font-bold text-white uppercase tracking-widest">Problem Solving</span></div>
              <h1 className="text-3xl md:text-5xl font-black leading-[1.1] mb-6">{content.title}</h1>
              <p className="text-xl text-gray-300 font-medium leading-relaxed">{content.description}</p>
            </div>
            <div className="space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">CHECKLIST</p>
              {content.checklist.map((item, i) => (
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 * i }} key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/20 transition-colors"><div className={`shrink-0 p-2 rounded-lg bg-gradient-to-br ${data.gradient} bg-opacity-10 text-white`}>{item.icon}</div><span className="font-medium text-gray-200">{item.title}</span></motion.div>
              ))}
            </div>
            <div className="p-6 rounded-3xl bg-blue-500/5 border border-blue-500/20 flex gap-4 items-center"><div className="p-3 bg-blue-500/20 rounded-full text-blue-400 shrink-0"><Lightbulb className="w-6 h-6"/></div><div><p className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-1">RESULT</p><p className="text-sm md:text-base text-blue-100 font-medium leading-relaxed">{content.result}</p></div></div>
            {content.summary && <p className="text-sm text-gray-400 italic leading-relaxed border-l-2 border-white/10 pl-4">{content.summary}</p>}
          </div>
          <div className="lg:sticky lg:top-8">
            <FormCard texts={t} btnText={content.cta} onSubmit={onSubmit} loading={loading} formName={content.formName} />
            <p className="text-[10px] text-gray-600 text-center mt-4 max-w-sm mx-auto">{t.auditDisclaimer}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ThankYou = ({ onHome, t }) => {
  useEffect(() => { trackEvent('CompleteRegistration'); }, []);
  return (
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="min-h-[100dvh] flex flex-col items-center justify-center text-center px-4">
      <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mb-8 shadow-[0_0_60px_-10px_rgba(34,197,94,0.5)]"><CheckCircle2 className="w-12 h-12 text-white" /></div>
      <h2 className="text-4xl md:text-5xl font-black mb-6 tracking-tight">{t.thankTitle}</h2>
      <p className="text-xl text-gray-400 max-w-md mb-12 font-medium leading-relaxed">{t.thankDesc}</p>
      <button onClick={onHome} className="px-10 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full font-bold transition-all text-white text-base">{t.home}</button>
    </motion.div>
  );
};

const useAnalytics = () => {
  const [milestones, setMilestones] = useState({ 25: false, 50: false, 90: false });
  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = (window.scrollY + window.innerHeight) / document.body.scrollHeight * 100;
      if (scrollPercent > 25 && !milestones[25]) { trackCustom('Scroll_25%'); setMilestones(p => ({...p, 25: true})); }
      if (scrollPercent > 50 && !milestones[50]) { trackCustom('Scroll_50%'); setMilestones(p => ({...p, 50: true})); }
      if (scrollPercent > 90 && !milestones[90]) { trackCustom('Scroll_90%'); setMilestones(p => ({...p, 90: true})); }
    };
    const timer = setTimeout(() => trackCustom('TimeOnSite_30s'), 30000);
    window.addEventListener('scroll', handleScroll);
    return () => { window.removeEventListener('scroll', handleScroll); clearTimeout(timer); };
  }, [milestones]);
};

function App() {
  const getInitialView = () => {
    const path = window.location.pathname;
    if (path === ROUTES.thankyou) return 'thankyou';
    if (path === ROUTES.select) return 'select';
    if (path === ROUTES.leads) return 'leads';
    if (path === ROUTES.sales) return 'sales';
    if (path === ROUTES.start) return 'start';
    return 'hero';
  };

  const [view, setView] = useState(getInitialView);
  const [loading, setLoading] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [legalType, setLegalType] = useState('privacy');
  const [showCookies, setShowCookies] = useState(false);

  const t = uiText;
  useAnalytics();

  const navigate = useCallback((newView) => {
    const path = ROUTES[newView] || '/';
    window.history.pushState({}, '', path);
    setView(newView);
    window.scrollTo(0, 0);
    if (window.gtag) window.gtag('config', 'G-1V7Q802W4S', { page_path: path });
    if (window.fbq) window.fbq('track', 'PageView');
  }, []);

  useEffect(() => {
    const handlePopState = () => setView(getInitialView());
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) setShowCookies(true);
  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem('cookieConsent', 'true');
    setShowCookies(false);
  };

  const handleSubmit = async (e, formData, formName) => {
    setLoading(true);
    const message = `🚀 <b>НОВА ЛІД-ЗАЯВКА</b>\n\n📌 <b>Форма:</b> ${formName}\n👤 <b>Ім'я:</b> ${formData.name}\n📱 <b>Телефон:</b> ${formData.phone}\n💬 <b>TG:</b> ${formData.extra || '-'}\n📧 <b>Email:</b> ${formData.email || '-'}`;
    try {
      if (TG_CONFIG.botToken !== "ВАШ_ТОКЕН_БОТА") {
        await fetch(`https://api.telegram.org/bot${TG_CONFIG.botToken}/sendMessage`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chat_id: TG_CONFIG.chatId, text: message, parse_mode: 'HTML' })
        });
      }
      trackEvent('Lead', { content_name: formName, currency: 'UAH', value: 0.00, user_data: { em: formData.email, ph: formData.phone } });
      navigate('thankyou');
    } catch (error) {
      alert("Помилка відправки.");
    } finally {
      setLoading(false);
    }
  };

  const openLegal = (type) => { trackCustom('ViewLegalInfo', { type }); setLegalType(type); setLegalOpen(true); };

return (
    <div className="App stable-height font-sans selection:bg-blue-500 selection:text-white relative bg-black text-base overflow-x-hidden">
      <Background />

      <AnimatePresence mode="wait">
        {view === 'hero' && (
          <div className="pb-24">
            <Hero key="hero" onNext={() => navigate('select')} t={t} />
          </div>
        )}

        {view === 'select' && (
          <div className="pb-24">
            <Selection key="select" onSelect={(key) => navigate(key)} t={t} />
          </div>
        )}

        {['leads', 'sales', 'start'].includes(view) && (
          <div className="pb-24">
            <DetailPage key="detail" type={view} onBack={() => navigate('select')} onSubmit={handleSubmit} t={t} loading={loading} />
          </div>
        )}

        {view === 'thankyou' && (
          <div className="pb-24">
            <ThankYou key="thankyou" onHome={() => navigate('hero')} t={t} />
          </div>
        )}
      </AnimatePresence>

      <footer className="fixed bottom-0 w-full py-6 text-center pointer-events-none z-50 bg-gradient-to-t from-black via-black/80 to-transparent">
        <div className="pointer-events-auto inline-flex gap-6 text-[10px] font-bold uppercase tracking-widest text-gray-400 bg-zinc-900/80 backdrop-blur-md px-6 py-2.5 rounded-full border border-white/10 shadow-2xl hover:border-white/30 transition-colors">
          <button onClick={() => openLegal('info')} className="hover:text-white transition-colors">{t.legalInfo}</button>
          <div className="w-px h-3 bg-white/10 self-center"></div>
          <button onClick={() => openLegal('privacy')} className="hover:text-white transition-colors">{t.privacy}</button>
        </div>
      </footer>

      <AnimatePresence>
        {legalOpen && <LegalModal isOpen={legalOpen} onClose={() => setLegalOpen(false)} type={legalType} />}
        {showCookies && <CookieBanner onAccept={handleAcceptCookies} />}
      </AnimatePresence>
    </div>
  );
}

export default App;